import { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { P } from '@/design-system/palette';
import { sans } from '@/design-system/typography';
import { Ico, L } from '@/design-system/icons';
import { motion } from '@/design-system/motion';
import { useUIStore } from '@/stores/ui-store';
import { BudgetContextMenu } from '@/pages/budget/components/BudgetContextMenu';

const CLIPBOARD = [
  'M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2',
  'M15 2H9a1 1 0 0 0-1 1v2a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V3a1 1 0 0 0-1-1z',
];

interface TabDef {
  path: string;
  iconPaths: string[];
  labelKey: string;
  larger?: boolean;
}

const TABS: TabDef[] = [
  { path: '/dashboard', iconPaths: [...L.lines], labelKey: 'nav.dashboard' },
  { path: '/budget', iconPaths: CLIPBOARD, labelKey: 'nav.budget', larger: true },
  { path: '/patrimoine', iconPaths: [...L.shield], labelKey: 'nav.patrimoine' },
  { path: '/objectifs', iconPaths: [...L.target], labelKey: 'nav.objectifs' },
];

function useScrollDirection(): 'up' | 'down' {
  const [dir, setDir] = useState<'up' | 'down'>('up');

  useEffect(() => {
    let lastY = window.scrollY;
    const onScroll = () => {
      const y = window.scrollY;
      if (Math.abs(y - lastY) > 5) {
        setDir(y > lastY ? 'down' : 'up');
        lastY = y;
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return dir;
}

function getNavKey(pathname: string): string {
  if (pathname.startsWith('/budget')) return 'nav.budget';
  if (pathname.startsWith('/patrimoine')) return 'nav.patrimoine';
  if (pathname.startsWith('/objectifs')) return 'nav.objectifs';
  if (pathname.startsWith('/parametres')) return 'nav.parametres';
  return 'nav.dashboard';
}

export function MobileLayout() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const editingEntity = useUIStore((s) => s.editingEntity);
  const scrollDir = useScrollDirection();

  const isBudgetPage = pathname.startsWith('/budget');
  const tabBarHidden = editingEntity !== null;
  const tabBarSlid = scrollDir === 'down';

  return (
    <div style={{ minHeight: '100dvh', background: P.s0 }}>
      <header style={{
        position: 'sticky',
        top: 0,
        zIndex: 30,
        height: 48,
        background: P.s0,
        borderBottom: `1px solid ${P.border}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0 16px',
        fontFamily: sans,
      }}>
        <span style={{ color: P.t1, fontSize: 15, fontWeight: 600 }}>
          {t(getNavKey(pathname))}
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
          {isBudgetPage && !editingEntity && <BudgetContextMenu />}
          <button
            onClick={() => navigate('/parametres')}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: 4,
              minWidth: 44,
              minHeight: 44,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Ico paths={[...L.settingsArr]} size={20} color={P.t2} />
          </button>
        </div>
      </header>

      <main style={{ paddingBottom: 'calc(56px + env(safe-area-inset-bottom))' }}>
        <Outlet />
      </main>

      {!tabBarHidden && (
        <nav style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          zIndex: 30,
          width: '100%',
          height: 56,
          paddingBottom: 'env(safe-area-inset-bottom)',
          background: P.s0,
          borderTop: `1px solid ${P.border}`,
          display: 'flex',
          justifyContent: 'space-around',
          alignItems: 'center',
          transform: tabBarSlid ? 'translateY(100%)' : 'translateY(0)',
          transition: 'transform 0.3s ease',
        }}>
          {TABS.map((tab) => {
            const active = pathname.startsWith(tab.path);
            const color = active ? P.accent : P.t3;
            return (
              <button
                key={tab.path}
                onClick={() => navigate(tab.path)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 2,
                  padding: '4px 0',
                  minWidth: 44,
                  minHeight: 44,
                  position: 'relative',
                }}
              >
                <Ico paths={tab.iconPaths} size={tab.larger ? 24 : 20} color={color} />
                <span style={{
                  fontFamily: sans,
                  fontSize: 9,
                  fontWeight: 500,
                  color,
                  lineHeight: 1,
                  transition: `color ${motion.duration.fast} ${motion.easing.default}`,
                }}>
                  {t(tab.labelKey)}
                </span>
                {active && (
                  <span style={{
                    position: 'absolute',
                    top: 0,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: 16,
                    height: 2,
                    borderRadius: 1,
                    background: P.accent,
                    animation: `cp-tab-indicator ${motion.duration.normal} ${motion.easing.spring}`,
                  }} />
                )}
              </button>
            );
          })}
        </nav>
      )}
    </div>
  );
}
